import { IconButton, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import LoadingButton from "@/components/common/buttons/loadingButton";
import { MinusCircleIcon, PlusCircleIcon } from "@/components/common/icons";
import {
  GREY,
  PRIMARY,
  TEXT_PRIMARY_DARK,
  TEXT_PRIMARY_WHITE,
  TEXT_SECONDARY,
} from "@/config/mui/colorPalette";
import { formattedPrice } from "@/utils";

type StoreButtonProps = {
  packageQuantity: number;
  onAmountChange: (change: 1 | -1) => void;
  onInputChange: (value: string) => void;
  unitPrice: number;
  isMobile: boolean;
  customizationEnabled: boolean;
  customizationPrimaryColor: string;
  type: "checkout" | "card";
};

export default function PackageCardStoreButton({
  packageQuantity,
  onAmountChange,
  onInputChange,
  unitPrice,
  isMobile,
  customizationEnabled,
  customizationPrimaryColor,
  type,
}: StoreButtonProps) {
  const buttonSize = isMobile ? "32" : "24";
  const [isTyping, setIsTyping] = useState(false);

  const isCardType = type === "card";

  if (packageQuantity > 0 || isTyping) {
    return (
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            ml: -1,
          }}
        >
          <IconButton
            disabled={packageQuantity === 0}
            onClick={(e) => {
              onAmountChange(-1);
              e.stopPropagation();
            }}
          >
            <MinusCircleIcon
              color={
                packageQuantity === 0
                  ? GREY[70]
                  : customizationEnabled
                    ? customizationPrimaryColor
                    : PRIMARY
              }
              size={buttonSize}
            />
          </IconButton>

          <TextField
            sx={{
              "& .MuiInputBase-input": {
                MozAppearance: "textfield",
                width: "48px",
                height: isCardType ? "40px" : "32px",
                padding: 0,
                textAlign: "center",
              },
              "& .MuiInputBase-input::-webkit-outer-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "& .MuiInputBase-input::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
            }}
            type="number"
            size="small"
            value={packageQuantity ? packageQuantity : ""}
            onChange={(e) => {
              setIsTyping(true);
              onInputChange(e.target.value);
            }}
            onBlur={(e) => {
              setIsTyping(false);
              if (e.target.value === "" || +e.target.value === 0) {
                onInputChange("1");
              }
            }}
            slotProps={{
              input: {
                sx: {
                  borderColor: GREY[50],
                },
              },
            }}
          />

          <IconButton
            onClick={(e) => {
              onAmountChange(1);
              e.stopPropagation();
            }}
          >
            <PlusCircleIcon
              color={customizationEnabled ? customizationPrimaryColor : PRIMARY}
              size={buttonSize}
            />
          </IconButton>
        </Stack>
        <Typography
          variant={isCardType ? "subtitleDefault" : "paragraphSmall"}
          sx={{
            color: isCardType ? TEXT_PRIMARY_DARK : TEXT_SECONDARY,
          }}
        >
          {isCardType
            ? formattedPrice(packageQuantity * unitPrice)
            : `x${formattedPrice(unitPrice)}`}
        </Typography>
      </Stack>
    );
  }

  if (packageQuantity === 0 && isCardType) {
    return (
      <LoadingButton
        variant="contained"
        size="small"
        onClick={(e) => {
          onAmountChange(1);
          e.stopPropagation();
        }}
      >
        <Typography
          variant={isMobile ? "buttonDefault" : "buttonSmall"}
          sx={{
            color: TEXT_PRIMARY_WHITE,
          }}
        >
          Add to cart
        </Typography>
      </LoadingButton>
    );
  }
}
